export type DateRangePreset =
  | "all_time"
  | "today"
  | "yesterday"
  | "last_7_days"
  | "last_30_days"
  | "this_month"
  | "last_month"
  | "custom";

export interface DateRangeValue {
  preset: DateRangePreset;
  from: Date;
  to: Date;
}

export const DATE_RANGE_OPTIONS: Array<{
  label: string;
  value: DateRangePreset;
}> = [
  { label: "All Time", value: "all_time" },
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "last_7_days" },
  { label: "Last 30 days", value: "last_30_days" },
  { label: "This month", value: "this_month" },
  { label: "Last month", value: "last_month" },
  { label: "Custom", value: "custom" },
];

export const getPresetDateRange = (
  preset: Exclude<DateRangePreset, "custom">,
  baseDate: Date = new Date(),
): DateRangeValue => {
  const today = startOfDay(baseDate);

  let from = today;
  let to = today;
  switch (preset) {
    case "all_time":
      from = today;
      to = today;
      break;
    case "today":
      from = today;
      to = today;
      break;
    case "yesterday":
      from = addDays(today, -1);
      to = addDays(today, -1);
      break;
    case "last_7_days":
      from = addDays(today, -6);
      to = today;
      break;
    case "last_30_days":
      from = addDays(today, -29);
      to = today;
      break;
    case "this_month":
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = today;
      break;
    case "last_month":
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      to = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
  }

  return { preset, from, to };
};

export const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const endOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

export const formatDateForApi = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

export const formatDateForDisplay = (date: Date) => {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
};

export const formatDateForInputDisplay = (date: Date | null) => {
  if (!date) return "DD/MM/YYYY";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

export const isSameDay = (first: Date, second: Date) =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate();

export const isDateBetween = (date: Date, from: Date, to: Date) => {
  const day = startOfDay(date).getTime();
  return day >= startOfDay(from).getTime() && day <= startOfDay(to).getTime();
};

export const createMonthGrid = (monthDate: Date) => {
  const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - monthStart.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);

    return {
      date,
      isCurrentMonth: date.getMonth() === monthDate.getMonth(),
    };
  });
};

export const addMonths = (date: Date, months: number) => {
  const nextDate = new Date(date);
  nextDate.setMonth(nextDate.getMonth() + months, 1);
  return nextDate;
};

export const formatMonthTitle = (date: Date) => {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
  }).format(date);
};

export const normalizeDateRange = (first: Date, second: Date) => {
  if (startOfDay(first).getTime() <= startOfDay(second).getTime()) {
    return { from: first, to: second };
  }

  return { from: second, to: first };
};
